import { queryGuidedTourElement } from './GuidedTour.domUtils';
import type {
  GuidedTourDefinition,
  GuidedTourStatus,
  GuidedTourStep,
  GuidedTourStoredState,
  GuidedTourStoredStatus,
  GuidedTourStoredStatuses,
} from './GuidedTour.types';

export const GUIDED_TOUR_STORAGE_KEY = 'guidedTourStatusByUser';
export const EMPTY_GUIDED_TOUR_STATUSES: GuidedTourStoredStatuses = {};
export const EMPTY_GUIDED_TOUR_STORED_STATE: GuidedTourStoredState = {
  statuses: EMPTY_GUIDED_TOUR_STATUSES,
};

const isGuidedTourStoredStatus = (status: unknown): status is GuidedTourStoredStatus =>
  status === 'completed' || status === 'dismissed';

export const getGuidedTourStepTarget = (step: GuidedTourStep): HTMLElement | null => {
  if (!step.target || typeof document === 'undefined') {
    return null;
  }

  return queryGuidedTourElement(step.target);
};

export const normalizeGuidedTourStoredStatuses = (value: unknown): GuidedTourStoredStatuses => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return EMPTY_GUIDED_TOUR_STATUSES;
  }

  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).filter(([, tourStatus]) =>
      isGuidedTourStoredStatus(tourStatus))
  ) as GuidedTourStoredStatuses;
};

export const normalizeGuidedTourStoredState = (value: unknown): GuidedTourStoredState => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return EMPTY_GUIDED_TOUR_STORED_STATE;
  }

  if ('statuses' in value) {
    const storedState = value as Partial<GuidedTourStoredState>;

    return {
      statuses: normalizeGuidedTourStoredStatuses(storedState.statuses),
    };
  }

  return {
    statuses: normalizeGuidedTourStoredStatuses(value),
  };
};

export const getNextGuidedTourStoredStatuses = (
  statuses: GuidedTourStoredStatuses,
  tourId: string,
  status: GuidedTourStatus
) => (isGuidedTourStoredStatus(status)
  ? { ...statuses, [tourId]: status }
  : statuses);

export const getNextGuidedTourStatusState = (
  storedState: GuidedTourStoredState,
  tourId: string,
  status: GuidedTourStatus
): GuidedTourStoredState => ({
  ...storedState,
  statuses: getNextGuidedTourStoredStatuses(storedState.statuses, tourId, status),
});

export const getVisibleTourStepIndexes = (tour: GuidedTourDefinition) =>
  tour.steps.reduce<number[]>((indexes, step, index) => {
    if (!step.canShow || step.canShow()) {
      indexes.push(index);
    }

    return indexes;
  }, []);

export const getFirstVisibleStepIndex = (tour: GuidedTourDefinition) =>
  getVisibleTourStepIndexes(tour)[0] ?? -1;

export const getNextVisibleStepIndex = (tour: GuidedTourDefinition, activeStepIndex: number) =>
  getVisibleTourStepIndexes(tour).find(index => index > activeStepIndex) ?? -1;

export const getPreviousVisibleStepIndex = (tour: GuidedTourDefinition, activeStepIndex: number) => {
  const previousIndexes = getVisibleTourStepIndexes(tour).filter(index => index < activeStepIndex);

  return previousIndexes.at(-1) ?? -1;
};
